import React, { useState, useEffect, useMemo } from 'react';
import { Users, CheckCircle, XCircle, Download, Phone, Calendar, Baby, Edit, Trash2, ArrowUpDown, Filter, Search, LogOut, Clock, Camera } from 'lucide-react';
import { RSVPResponse, RSVPStats } from '../types/rsvp';
import { getRSVPResponses, getRSVPStats, exportToCSV, updateRSVPResponse, deleteRSVPResponse } from '../utils/storage';
import { logoutAdmin, getSessionTimeRemaining, extendAdminSession } from '../utils/auth';
import EditRSVPModal from './EditRSVPModal';
import { PhotoGallery } from './PhotoGallery';


interface AdminDashboardProps {
  onBack: () => void;
}

type SortField = 'guestName' | 'phone' | 'attending' | 'guestCount' | 'weddingCode' | 'submittedAt';
type SortDirection = 'asc' | 'desc';
type FilterStatus = 'all' | 'attending' | 'not-attending';
type FilterCode = 'all' | 'other';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onBack }) => {
  const [responses, setResponses] = useState<RSVPResponse[]>([]);
  const [stats, setStats] = useState<RSVPStats>({ totalResponses: 0, attendingGuests: 0 });
  const [selectedResponse, setSelectedResponse] = useState<RSVPResponse | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState<RSVPResponse | null>(null);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'rsvp' | 'photos'>('rsvp');
  
  // Sorting and filtering state
  const [sortField, setSortField] = useState<SortField>('submittedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterCode, setFilterCode] = useState<FilterCode>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Cache duration: 30 seconds
  const CACHE_DURATION = 30000;

  const handleManualRefresh = async () => {
    const now = Date.now();
    try {
      setIsLoading(true);
      const [responsesData, statsData] = await Promise.all([
        getRSVPResponses(),
        getRSVPStats()
      ]);
      setResponses(responsesData);
      setStats(statsData);
      setLastFetchTime(now);
    } catch (error) {
      // Handle error silently
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async (forceRefresh = false) => {
      const now = Date.now();
      
      // Skip if data is fresh and not forcing refresh
      if (!forceRefresh && now - lastFetchTime < CACHE_DURATION && responses.length > 0) {
        return;
      }
      
      try {
        setIsLoading(true);
        const [responsesData, statsData] = await Promise.all([
          getRSVPResponses(),
          getRSVPStats()
        ]);
        setResponses(responsesData);
        setStats(statsData);
        setLastFetchTime(now);
      } catch (error) {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData(true); // Force initial load
    const interval = setInterval(() => loadData(false), 30000); // Auto-refresh every 30 seconds (reduced frequency)
    return () => clearInterval(interval);
  }, []);

  // Session management
  useEffect(() => {
    const updateSessionTime = () => {
      const remaining = getSessionTimeRemaining();
      setSessionTimeRemaining(remaining);
      
      // Auto-logout when session expires
      if (remaining <= 0) {
        logoutAdmin();
      }
      
      // Extend session on activity (every 30 minutes)
      if (remaining > 0 && remaining < 90) { // Less than 1.5 hours remaining
        extendAdminSession();
      }
    };
    
    updateSessionTime();
    const sessionInterval = setInterval(updateSessionTime, 60000); // Check every minute
    return () => clearInterval(sessionInterval);
  }, []);

  const handleExport = async () => {
    try {
      const csvData = await exportToCSV();
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wedding-rsvp-responses-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Handle error silently
      alert('Failed to export data. Please try again.');
    }
  };

  const handleEdit = (response: RSVPResponse) => {
    setEditingResponse(response);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedResponse: RSVPResponse) => {
    try {
      // Optimistic update
      const optimisticResponses = responses.map(r => 
        r.id === updatedResponse.id ? updatedResponse : r
      );
      setResponses(optimisticResponses);
      
      // Recalculate stats optimistically
      const attendingResponses = optimisticResponses.filter(r => r.attending);
      const optimisticStats = {
        totalResponses: optimisticResponses.length,
        attendingGuests: attendingResponses.reduce((sum, r) => sum + r.guestCount, 0)
      };
      setStats(optimisticStats);
      
      await updateRSVPResponse(updatedResponse.id, updatedResponse);
      setIsEditModalOpen(false);
      setEditingResponse(null);
      
      // Update cache timestamp
      setLastFetchTime(Date.now());
    } catch (error) {
      // Handle error silently
      // Revert optimistic update on error
      const [responsesData, statsData] = await Promise.all([
        getRSVPResponses(),
        getRSVPStats()
      ]);
      setResponses(responsesData);
      setStats(statsData);
      throw error;
    }
  };

  const handleDelete = async (response: RSVPResponse) => {
    if (window.confirm(`Are you sure you want to delete the RSVP response from ${response.guestName}? This action cannot be undone.`)) {
      try {
        // Optimistic update - remove from UI immediately
        const optimisticResponses = responses.filter(r => r.id !== response.id);
        setResponses(optimisticResponses);
        
        // Recalculate stats optimistically
        const attendingResponses = optimisticResponses.filter(r => r.attending);
        const optimisticStats = {
          totalResponses: optimisticResponses.length,
          attendingGuests: attendingResponses.reduce((sum, r) => sum + r.guestCount, 0)
        };
        setStats(optimisticStats);
        
        // Perform actual deletion in background
        await deleteRSVPResponse(response.id);
        
        // Update cache timestamp
        setLastFetchTime(Date.now());
      } catch (error) {
        // Handle error silently
        alert('Failed to delete RSVP response. Please try again.');
        // Revert optimistic update on error
        const [responsesData, statsData] = await Promise.all([
          getRSVPResponses(),
          getRSVPStats()
        ]);
        setResponses(responsesData);
        setStats(statsData);
      }
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingResponse(null);
  };

  // Sorting and filtering logic
  const filteredAndSortedResponses = useMemo(() => {
    let filtered = responses;

    // Apply status filter
    if (filterStatus === 'attending') {
      filtered = filtered.filter(r => r.attending);
    } else if (filterStatus === 'not-attending') {
      filtered = filtered.filter(r => !r.attending);
    }

    // Apply code filter
    if (filterCode !== 'all') {
      // All current codes are valid wedding codes, so 'other' would be test codes or invalid codes
      const validWeddingCodes = [
        'domkym25', 'arsanau25', 'petcec25', 'yvonlex25', 'josiah25', 'gabriel25',
        'sham25', 'frank25', 'chaifel25', 'ronbeck25', 'husagn25', 'rickrys25',
        'suhudaw25', 'casspri25', 'andwin25', 'chagat25', 'ramos25', 'vivi25',
        'jane25', 'banmicmay25', 'roslyn25', 'chelaar25', 'benj25', 'thiasel25',
        'limjan25', 'ryumiz25', 'marctab25', 'izurei25', 'jonstep25', 'denhae25',
        'angmuj25', 'desman25', 'anndavdav25', 'dickhel25', 'yumryo25', 'kongjoa25',
        'shinmik25', 'kohtam25', 'hyoin25', 'sheena25', 'stevlis25', 'mikdeb25',
        'chinang25', 'johsue25', 'thanya25', 'dama25', 'lazarus25', 'shanlim25',
        'simnancy25', 'matlis25', 'shuena25', 'bardar25', 'patrick25', 'randy25',
        'nancy25', 'yin25', 'hos25', 'anddol25', 'kijseon25', 'koiyur25',
        'nanami25', 'kana25', 'inikung25', 'hapyii25', 'yianmei25', 'edwyn25',
        // New Filler Groups
        'kadik25', 'pendi25', 'yosua25', 'elah25', 'chiharu25', 'gawai25'
      ];
      
      if (filterCode === 'other') {
        filtered = filtered.filter(r => !validWeddingCodes.includes(r.weddingCode));
      }
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.guestName.toLowerCase().includes(term) ||
        r.phone.includes(term) ||
        r.weddingCode.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle special cases
      if (sortField === 'submittedAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortField === 'attending') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [responses, sortField, sortDirection, filterStatus, filterCode, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Enhanced statistics with filtering
  const filteredStats = useMemo(() => {
    const filtered = filteredAndSortedResponses;
    const attending = filtered.filter(r => r.attending);
    
    return {
      totalFiltered: filtered.length,
      attendingFiltered: attending.length,
      notAttendingFiltered: filtered.length - attending.length,
      totalGuestsFiltered: attending.reduce((sum, r) => sum + r.guestCount, 0),
      adultsFiltered: attending.reduce((sum, r) => sum + r.adultCount, 0),
      childrenFiltered: attending.reduce((sum, r) => sum + r.childCount, 0)
    };
  }, [filteredAndSortedResponses]);

  // Calculate attending and not attending counts from responses
  const attendingCount = responses.filter(r => r.attending).length;
  const notAttendingCount = responses.filter(r => !r.attending).length;
  const totalResponsesCount = attendingCount + notAttendingCount;
  
  // Calculate total guests attending (sum of guestCount for attending responses)
  const totalGuestsAttending = responses
    .filter(r => r.attending)
    .reduce((sum, r) => sum + r.guestCount, 0);

  // Calculate unused wedding codes
  const allWeddingCodes = [
    'domkym25', 'arsanau25', 'petcec25', 'yvonlex25', 'josiah25', 'gabriel25',
    'sham25', 'frank25', 'chaifel25', 'ronbeck25', 'husagn25', 'rickrys25',
    'suhudaw25', 'casspri25', 'andwin25', 'chagat25', 'ramos25', 'vivi25',
    'jane25', 'banmicmay25', 'roslyn25', 'chelaar25', 'benj25', 'thiasel25',
    'limjan25', 'ryumiz25', 'marctab25', 'izurei25', 'jonstep25', 'denhae25',
    'angmuj25', 'desman25', 'anndavdav25', 'dickhel25', 'yumryo25', 'kongjoa25',
    'shinmik25', 'kohtam25', 'hyoin25', 'sheena25', 'stevlis25', 'mikdeb25',
    'chinang25', 'johsue25', 'thanya25', 'dama25', 'lazarus25', 'shanlim25',
    'simnancy25', 'matlis25', 'shuena25', 'bardar25', 'patrick25', 'randy25',
    'nancy25', 'yin25', 'hos25', 'anddol25', 'andmun25', 'kijseon25', 'koiyur25',
    'nanami25', 'kana25', 'inikung25', 'hapyii25', 'yianmei25', 'edwyn25',
    // New Filler Groups
    'kadik25', 'pendi25', 'yosua25', 'elah25', 'chiharu25', 'gawai25'
  ];
  
  const usedCodes = new Set(responses.map(r => r.weddingCode));
  const unusedCodes = allWeddingCodes.filter(code => !usedCodes.has(code));
  
  // Check if it's been more than 10 days since a reference date (e.g., when codes were distributed)
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
  const showUnusedCodes = true; // Always show for now, can be modified based on actual distribution date

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex flex-col space-y-4 lg:flex-row lg:justify-between lg:items-start lg:space-y-0">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">RSVP Admin Dashboard</h1>
                <p className="text-gray-600">Manage wedding responses and guest information</p>
              </div>
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                {/* Session Timer */}
                <div className="flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                  <Clock className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Session: {Math.floor(sessionTimeRemaining / 60)}h {sessionTimeRemaining % 60}m</span>
                  <span className="sm:hidden">{Math.floor(sessionTimeRemaining / 60)}h {sessionTimeRemaining % 60}m</span>
                </div>
                {/* Logout Button */}
                <button
                  onClick={logoutAdmin}
                  className="flex items-center justify-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0 mb-6">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors w-fit"
            >
              ← Back
            </button>
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
              {isLoading && (
                 <div className="flex items-center text-sm text-blue-600">
                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                   Loading...
                 </div>
               )}
               <button
                 onClick={handleManualRefresh}
                 className="flex items-center justify-center bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 disabled={isLoading}
                 title="Refresh data"
               >
                 <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                 </svg>
                 <span className="hidden sm:inline">Refresh</span>
               </button>
               <button
                 onClick={handleExport}
                 className="flex items-center justify-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 disabled={isLoading}
               >
                 <Download className="w-4 h-4 mr-2" />
                 <span className="hidden sm:inline">Export CSV</span>
                 <span className="sm:hidden">Export</span>
               </button>
            </div>
          </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Total Responses</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalResponsesCount}</p>
                {filteredStats.totalFiltered !== totalResponsesCount && (
                  <p className="text-xs text-blue-600">Filtered: {filteredStats.totalFiltered}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Attending Responses</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{attendingCount}</p>
                {filteredStats.attendingFiltered !== attendingCount && (
                  <p className="text-xs text-green-600">Filtered: {filteredStats.attendingFiltered}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-emerald-100 rounded-lg flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Total Guests Attending</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalGuestsAttending}</p>
                {filteredStats.totalGuestsFiltered !== totalGuestsAttending && (
                  <p className="text-xs text-emerald-600">Filtered: {filteredStats.totalGuestsFiltered}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-red-100 rounded-lg flex-shrink-0">
                <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Not Attending</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{notAttendingCount}</p>
                {filteredStats.notAttendingFiltered !== notAttendingCount && (
                  <p className="text-xs text-red-600">Filtered: {filteredStats.notAttendingFiltered}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-4 sm:gap-8 px-4 sm:px-6">
              <button
                onClick={() => setActiveTab('rsvp')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'rsvp'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">RSVP Responses</span>
                  <span className="sm:hidden">RSVP</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('photos')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'photos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Camera className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Wedding Photos</span>
                  <span className="sm:hidden">Photos</span>
                </div>
              </button>

            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'rsvp' && (
          <>
            {/* Wedding Codes Status Section */}
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Users className="w-5 h-5 mr-2 text-blue-500" />
                Wedding Codes Status
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                All {allWeddingCodes.length} wedding codes with usage status
              </p>
            </div>
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4 lg:space-x-6">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-gray-600">{responses.filter(r => r.attending).length} Attending</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-2 flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-gray-600">{responses.filter(r => !r.attending).length} Not Attending</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded-full mr-2 flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-gray-600">{unusedCodes.length} Unused</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
             {allWeddingCodes.map((code) => {
               const isUsed = usedCodes.has(code);
               const usedResponse = responses.find(r => r.weddingCode === code);
               const displayName = isUsed ? (usedResponse?.guestName || 'Unknown') : code;
               const isAttending = usedResponse?.attending;
               
               let colorClasses, iconColor, icon;
               if (!isUsed) {
                 colorClasses = 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100';
                 iconColor = 'text-orange-600';
                 icon = <Clock className="w-3 h-3 text-orange-600 flex-shrink-0" />;
               } else if (isAttending) {
                 colorClasses = 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100';
                 iconColor = 'text-green-600';
                 icon = <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />;
               } else {
                  colorClasses = 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100';
                  iconColor = 'text-purple-600';
                  icon = <XCircle className="w-3 h-3 text-purple-600 flex-shrink-0" />;
                }
               
               return (
                 <div
                   key={code}
                   className={`px-2 py-2 sm:px-3 border rounded-lg text-xs sm:text-sm text-center transition-colors min-h-[2.5rem] flex items-center justify-center ${colorClasses}`}
                   title={isUsed ? `Code: ${code} - Used by: ${usedResponse?.guestName || 'Unknown'} (${usedResponse?.attending ? 'Attending' : 'Not Attending'})` : `Unused code: ${code}`}
                 >
                   <div className="flex flex-col items-center justify-center w-full">
                     <div className="flex items-center justify-center mb-1">
                       {icon}
                     </div>
                     <span className="truncate w-full text-center leading-tight" style={{fontSize: isUsed ? '0.65rem' : '0.75rem'}}>
                       {displayName}
                     </span>
                   </div>
                 </div>
               );
             })}
          </div>
          <div className="mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">
              <strong>Legend:</strong> Green codes are for guests attending, purple codes are for guests not attending, orange codes are still unused. 
              <span className="hidden sm:inline">Hover over each code to see details.</span>
              <span className="sm:hidden">Tap codes for details.</span>
            </p>
          </div>
        </div>

            {/* Search and Filter Controls */}
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or wedding code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 sm:items-center">
              {/* Status Filter */}
              <div className="flex items-center gap-2 flex-1 sm:flex-initial">
                <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                  className="flex-1 sm:flex-initial px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="attending">Attending Only</option>
                  <option value="not-attending">Not Attending Only</option>
                </select>
              </div>
              
              {/* Code Filter */}
              <div className="flex-1 sm:flex-initial">
                <select
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value as FilterCode)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Codes</option>
                  <option value="other">Test/Other Codes</option>
                </select>
              </div>
              
              {/* Clear Filters */}
              {(searchTerm || filterStatus !== 'all' || filterCode !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                    setFilterCode('all');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 w-full sm:w-auto"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

            {/* Responses Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">RSVP Responses</h2>
              <p className="text-sm text-gray-500 mt-1">
                Showing {filteredAndSortedResponses.length} of {responses.length} responses
              </p>
            </div>
          </div>
          
          {filteredAndSortedResponses.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              {responses.length === 0 ? (
                <>
                  <p className="mt-4 text-lg text-gray-500">No RSVP responses yet</p>
                  <p className="text-sm text-gray-400">Responses will appear here once guests start submitting</p>
                </>
              ) : (
                <>
                  <p className="mt-4 text-lg text-gray-500">No responses match your filters</p>
                  <p className="text-sm text-gray-400">Try adjusting your search or filter criteria</p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('guestName')}
                      >
                        <div className="flex items-center gap-1">
                          Guest Name
                          <ArrowUpDown className="w-3 h-3" />
                          {sortField === 'guestName' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('phone')}
                      >
                        <div className="flex items-center gap-1">
                          Phone
                          <ArrowUpDown className="w-3 h-3" />
                          {sortField === 'phone' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('attending')}
                      >
                        <div className="flex items-center gap-1">
                          Status
                          <ArrowUpDown className="w-3 h-3" />
                          {sortField === 'attending' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('guestCount')}
                      >
                        <div className="flex items-center gap-1">
                          Guest Count
                          <ArrowUpDown className="w-3 h-3" />
                          {sortField === 'guestCount' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adults/Children</th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('weddingCode')}
                      >
                        <div className="flex items-center gap-1">
                          Wedding Code
                          <ArrowUpDown className="w-3 h-3" />
                          {sortField === 'weddingCode' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('submittedAt')}
                      >
                        <div className="flex items-center gap-1">
                          Submitted
                          <ArrowUpDown className="w-3 h-3" />
                          {sortField === 'submittedAt' && (
                            <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedResponses.map((response) => (
                      <tr key={response.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-xs break-words">{response.guestName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Phone className="h-4 w-4 text-gray-400 mr-2" />
                            {response.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            response.attending 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {response.attending ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Attending</>
                            ) : (
                              <><XCircle className="w-3 h-3 mr-1" /> Not Attending</>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {response.guestCount} {response.guestCount === 1 ? 'guest' : 'guests'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {response.attending && (
                            <div className="flex items-center space-x-2">
                              <span className="flex items-center">
                                <Users className="w-3 h-3 mr-1 text-blue-500" />
                                {response.adultCount}
                              </span>
                              <span className="flex items-center">
                                <Baby className="w-3 h-3 mr-1 text-green-500" />
                                {response.childCount}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {response.weddingCode}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            Other
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            {new Date(response.submittedAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(response)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                              title="Edit RSVP"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(response)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                              title="Delete RSVP"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-gray-200">
                {filteredAndSortedResponses.map((response) => (
                  <div key={response.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 break-words">{response.guestName}</h3>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <Phone className="h-4 w-4 mr-1" />
                          {response.phone}
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        response.attending 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {response.attending ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Attending</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" /> Not Attending</>
                        )}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-500">Guests:</span>
                        <span className="ml-1 font-medium">{response.guestCount}</span>
                      </div>
                      {response.attending && (
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center">
                            <Users className="w-3 h-3 mr-1 text-blue-500" />
                            {response.adultCount}
                          </span>
                          <span className="flex items-center">
                            <Baby className="w-3 h-3 mr-1 text-green-500" />
                            {response.childCount}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Code:</span>
                        <span className="ml-1 font-mono text-xs bg-gray-100 px-2 py-1 rounded">{response.weddingCode}</span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-gray-600">{new Date(response.submittedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(response)}
                        className="flex items-center px-3 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors text-sm"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(response)}
                        className="flex items-center px-3 py-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors text-sm"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
            </div>
          </>
        )}

        {activeTab === 'photos' && (
           <PhotoGallery isAdmin={true} />
         )}


        </div>
      </div>
      
      {/* Edit RSVP Modal */}
      {editingResponse && (
        <EditRSVPModal
          response={editingResponse}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
};

export default AdminDashboard;